import { keys, isEmpty, isBoolean, isNumber, isNil, isFunction, cloneDeep } from 'lodash';
// Import Store
import { store, useAppDispatch } from 'store';
import { changePageValidation } from 'store/slices/pageValidation';
// Import Services
import { validationCases } from 'validators';
// Import Constants
import { ErrorShowType, AlertTypes } from 'constants/common';
// Import Hooks
import useToaster from './UseToaster';

const getPageValidationData = () => cloneDeep(store.getState().pageValidation.data);

interface GetCustomValidationTypeErrorTextParams {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    validationPropertyValue?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    validationCredential?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    validationResultValue?: any;
}

const useValidator = (
    getCustomValidationTypeErrorText: Nullable<(params: GetCustomValidationTypeErrorTextParams) => string> = null,
    ignoredFields: string[] = [],
) => {
    const dispatch = useAppDispatch();
    const { showToaster } = useToaster();

    const getErrorText = (validationPropertyValue, validationCredential, validationResultValue) => {
        if (isBoolean(validationResultValue)) {
            return validationCredential.ErrorMessage;
        } else if (isNumber(validationResultValue)) {
            if (isNil(validationCredential.ErrorMessages)) {
                return validationCredential.ErrorMessage;
            } else {
                return validationCredential.ErrorMessages[validationResultValue];
            }
        } else if (!isNil(getCustomValidationTypeErrorText) && isFunction(getCustomValidationTypeErrorText)) {
            return getCustomValidationTypeErrorText({
                validationPropertyValue,
                validationCredential,
                validationResultValue,
            });
        }

        return validationResultValue;
    };

    const getWarningText = (warningMessages: string[]) => {
        if (
            !isEmpty(warningMessages) &&
            !isNil(getCustomValidationTypeErrorText) &&
            isFunction(getCustomValidationTypeErrorText)
        ) {
            return getCustomValidationTypeErrorText({ validationResultValue: warningMessages });
        }
        return null;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const singleValidator = (label: string, value: any) => {
        // Getting actual store to make possible calling validate multiple times in a row
        const pageValidation = getPageValidationData();
        const validationCredentials = pageValidation[label]?.validationCredentials ?? [];

        let foundValidationCredential;
        let validationResultValue;
        let warningMessages: string[] = [];

        for (let index = 0; index < validationCredentials.length; index++) {
            const validationCredential = validationCredentials[index];
            validationResultValue = validationCases(validationCredential, value);
            if (validationResultValue) {
                if (!isNil(validationResultValue?.errorMessages)) {
                    if (!isEmpty(validationResultValue?.warningMessages)) {
                        warningMessages = [...warningMessages, ...validationResultValue.warningMessages];
                    }
                    if (!isEmpty(validationResultValue.errorMessages)) {
                        validationResultValue = validationResultValue.errorMessages;
                        foundValidationCredential = validationCredentials[index];
                        break;
                    }
                    continue;
                }

                foundValidationCredential = validationCredentials[index];
                break;
            }
        }

        const warningText = getWarningText(warningMessages);

        if (foundValidationCredential) {
            const errorText = getErrorText(value, foundValidationCredential, validationResultValue);

            return {
                ...pageValidation[label],
                isValid: false,
                errorText: errorText,
                warningText: warningText,
                paramValue: foundValidationCredential.ParametersValues,
                value,
                ...(foundValidationCredential.TranslationVariables && {
                    translationVariables: foundValidationCredential.TranslationVariables,
                }),
            };
        } else {
            return {
                ...pageValidation[label],
                isValid: true,
                errorText: '',
                value,
                warningText: warningText,
            };
        }
    };

    const validationChecker = (data, validateAll) => {
        let hasInvalid = false;
        let errorMessages = '';
        const pageValidation = getPageValidationData();

        const resultValidationScenario = keys(data).reduce((acc, key) => {
            if (pageValidation[key]) {
                const isValidated =
                    validateAll && pageValidation[key].isValid && pageValidation[key].forceValidation !== true
                        ? pageValidation[key]
                        : singleValidator(key, validateAll ? pageValidation[key].value : data[key]);
                acc[key] = isValidated;
                if (
                    !isValidated.isValid &&
                    key !== 'Name' &&
                    key !== 'Description' &&
                    ignoredFields.every((field) => !key.startsWith(field))
                ) {
                    hasInvalid = true;
                    if (isValidated.type === ErrorShowType.Toaster) {
                        errorMessages += isValidated.errorText + '\n';
                    }
                }
            }

            return acc;
        }, {});

        if (hasInvalid && validateAll && !isEmpty(errorMessages)) {
            showToaster(AlertTypes.error, errorMessages);
        }
        dispatch(changePageValidation(cloneDeep(resultValidationScenario)));
        return hasInvalid;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const validator = (data?: Nilable<any>) => {
        // Getting actual store to make possible calling validate multiple times in a row
        const pageValidation = getPageValidationData();
        if (!isEmpty(pageValidation)) {
            if (data) {
                return validationChecker(data, false);
            }
            return validationChecker(pageValidation, true);
        }
    };

    return validator;
};

export default useValidator;
